/**
 * Generator plików DXF - ULTRA MINIMALNA WERSJA
 * Tylko absolutne minimum do otwarcia w programach CAD
 */

import { CuttingLayout } from './sheetOptimizer';

export class DXFGenerator {
  
  /**
   * Generuje najprostszy możliwy plik DXF - tylko linie
   */
  generateFromLayout(layout: CuttingLayout): string {
    // Najprostszy możliwy DXF - tylko sekcja ENTITIES
    let dxf = '  0\nSECTION\n  2\nENTITIES\n';
    
    // Dodaj prostokąty jako pojedyncze linie
    layout.placedPieces.forEach((piece) => {
      const x1 = piece.x;
      const y1 = piece.y;
      const x2 = piece.x + piece.width;
      const y2 = piece.y + piece.height;
      
      // Górna linia
      dxf += this.line(x1, y1, x2, y1);
      // Prawa linia
      dxf += this.line(x2, y1, x2, y2);
      // Dolna linia
      dxf += this.line(x2, y2, x1, y2);
      // Lewa linia
      dxf += this.line(x1, y2, x1, y1);
    });
    
    dxf += '  0\nENDSEC\n  0\nEOF\n';
    
    return dxf;
  }
  
  /**
   * Generuje DXF z wieloma arkuszami
   */
  generateFromLayouts(layouts: CuttingLayout[]): string {
    let dxf = '  0\nSECTION\n  2\nENTITIES\n';
    
    let offsetX = 0;
    const spacing = 100;
    
    layouts.forEach((layout) => {
      // Obrys arkusza
      const sheetW = layout.sheet.width;
      const sheetH = layout.sheet.height;
      
      dxf += this.line(offsetX, 0, offsetX + sheetW, 0);
      dxf += this.line(offsetX + sheetW, 0, offsetX + sheetW, sheetH);
      dxf += this.line(offsetX + sheetW, sheetH, offsetX, sheetH);
      dxf += this.line(offsetX, sheetH, offsetX, 0);
      
      // Elementy
      layout.placedPieces.forEach((piece) => {
        const x1 = piece.x + offsetX;
        const y1 = piece.y;
        const x2 = x1 + piece.width;
        const y2 = y1 + piece.height;
        
        dxf += this.line(x1, y1, x2, y1);
        dxf += this.line(x2, y1, x2, y2);
        dxf += this.line(x2, y2, x1, y2);
        dxf += this.line(x1, y2, x1, y1);
      });
      
      offsetX += sheetW + spacing;
    });
    
    dxf += '  0\nENDSEC\n  0\nEOF\n';
    
    return dxf;
  }
  
  private line(x1: number, y1: number, x2: number, y2: number): string {
    return `  0\nLINE\n  8\n0\n 10\n${x1}\n 20\n${y1}\n 11\n${x2}\n 21\n${y2}\n`;
  }
}

/**
 * Generator G-code dla maszyn CNC
 */
export class GCodeGenerator {
  private feedRate: number = 1000; // mm/min
  private plungeRate: number = 300; // mm/min
  private cutDepth: number = 3; // mm na przejście
  private safeHeight: number = 10; // mm wysokość bezpieczna
  
  generateFromLayout(layout: CuttingLayout): string {
    const lines: string[] = [];
    
    // Nagłówek G-code
    lines.push(
      '; PlexiSystem CNC Cutting Program',
      `; Material: ${layout.sheet.material}`,
      `; Thickness: ${layout.sheet.thickness}mm`,
      `; Sheet: ${layout.sheet.width}x${layout.sheet.height}mm`,
      `; Generated: ${new Date().toISOString()}`,
      '',
      '; Initialize',
      'G21 ; Metric units',
      'G90 ; Absolute positioning',
      'G17 ; XY plane',
      'M3 S12000 ; Spindle on',
      `G0 Z${this.safeHeight} ; Safe height`,
      ''
    );
    
    // Generuj ścieżki cięcia
    layout.placedPieces.forEach((piece, index) => {
      lines.push(
        `; Piece ${index + 1}: ${piece.originalPiece.label || piece.id}`,
        `; Size: ${piece.width}x${piece.height}mm`
      );
      
      // Cięcie prostokąta
      const passes = Math.ceil(layout.sheet.thickness / this.cutDepth);
      
      for (let pass = 1; pass <= passes; pass++) {
        const depth = Math.min(pass * this.cutDepth, layout.sheet.thickness);
        
        lines.push(
          `; Pass ${pass}/${passes}, depth: ${depth}mm`,
          `G0 X${piece.x} Y${piece.y}`,
          `G0 Z${this.safeHeight}`,
          `G1 Z${-depth} F${this.plungeRate}`,
          `G1 X${piece.x + piece.width} Y${piece.y} F${this.feedRate}`,
          `G1 X${piece.x + piece.width} Y${piece.y + piece.height}`,
          `G1 X${piece.x} Y${piece.y + piece.height}`,
          `G1 X${piece.x} Y${piece.y}`,
          `G0 Z${this.safeHeight}`,
          ''
        );
      }
    });
    
    // Stopka G-code
    lines.push(
      '; End program',
      'G0 Z' + this.safeHeight,
      'M5 ; Spindle off',
      'G0 X0 Y0 ; Home',
      'M30 ; Program end',
      ''
    );
    
    return lines.join('\n');
  }
}